import React from 'react';

// Safelist for Tailwind CSS extractor (purging dynamic classes)
// w-1/12 w-2/12 w-3/12 w-4/12 w-5/12 w-6/12 w-7/12 w-8/12 w-9/12 w-10/12 w-11/12 w-full w-auto
// sm:w-1/12 sm:w-2/12 sm:w-3/12 sm:w-4/12 sm:w-5/12 sm:w-6/12 sm:w-7/12 sm:w-8/12 sm:w-9/12 sm:w-10/12 sm:w-11/12 sm:w-full sm:w-auto
// md:w-1/12 md:w-2/12 md:w-3/12 md:w-4/12 md:w-5/12 md:w-6/12 md:w-7/12 md:w-8/12 md:w-9/12 md:w-10/12 md:w-11/12 md:w-full md:w-auto
// lg:w-1/12 lg:w-2/12 lg:w-3/12 lg:w-4/12 lg:w-5/12 lg:w-6/12 lg:w-7/12 lg:w-8/12 lg:w-9/12 lg:w-10/12 lg:w-11/12 lg:w-full lg:w-auto
// xl:w-1/12 xl:w-2/12 xl:w-3/12 xl:w-4/12 xl:w-5/12 xl:w-6/12 xl:w-7/12 xl:w-8/12 xl:w-9/12 xl:w-10/12 xl:w-11/12 xl:w-full xl:w-auto
// 2xl:w-1/12 2xl:w-2/12 2xl:w-3/12 2xl:w-4/12 2xl:w-5/12 2xl:w-6/12 2xl:w-7/12 2xl:w-8/12 2xl:w-9/12 2xl:w-10/12 2xl:w-11/12 2xl:w-full 2xl:w-auto

// Grid container component (replaces Reactstrap Container)
interface ContainerProps {
  fluid?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const Container: React.FC<ContainerProps> = ({
  fluid = false,
  children,
  className = ''
}) => {
  const baseClass = fluid ? 'container-fluid' : 'container mx-auto';
  return <div className={`${baseClass} ${className}`}>{children}</div>;
};

// Row component (replaces Reactstrap Row)
interface RowProps {
  children: React.ReactNode;
  className?: string;
}

export const Row: React.FC<RowProps> = ({ children, className = '' }) => {
  return (
    <div className={`flex flex-wrap -mx-3 ${className}`}>
      {children}
    </div>
  );
};

// Col component (replaces Reactstrap Col)
type ColSize = number | 'auto';

interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  xs?: ColSize;
  sm?: ColSize;
  md?: ColSize;
  lg?: ColSize;
  xl?: ColSize;
  xxl?: ColSize;
}

const getColClass = (prefix: string, size: ColSize | undefined): string | null => {
  if (size === undefined) return null;
  if (size === 'auto') return `${prefix}w-auto`;
  if (size === 12) return `${prefix}w-full`;
  return `${prefix}w-${size}/12`;
};

export const Col: React.FC<ColProps> = ({
  children,
  className = '',
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  ...rest
}) => {
  // Convert Reactstrap col props to Tailwind classes
  const xsClass = getColClass('', xs);
  const smClass = getColClass('sm:', sm);
  const mdClass = getColClass('md:', md);
  const lgClass = getColClass('lg:', lg);
  const xlClass = getColClass('xl:', xl);
  const xxlClass = getColClass('2xl:', xxl);

  // Keep full-width base unless xs explicitly overrides it.
  const baseClass = xsClass ?? 'w-full';
  const responsiveClasses = [smClass, mdClass, lgClass, xlClass, xxlClass]
    .filter(Boolean)
    .join(' ');
  const widthClass = [baseClass, responsiveClasses].filter(Boolean).join(' ');

  return (
    <div className={`px-3 ${widthClass} ${className}`} {...rest}>
      {children}
    </div>
  );
};
